import React, { useEffect, useRef, useState } from "react";
import { api, apiErr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Camera, Upload, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function PhotoGallery({ requestId, canUpload }) {
  const [photos, setPhotos] = useState({ before: [], after: [] });
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();
  const [kind, setKind] = useState("after");

  const load = async () => {
    try {
      const { data } = await api.get(`/requests/${requestId}/photos-summary`);
      setPhotos(data);
    } catch (e) { console.warn("photos load failed", e); }
  };

  useEffect(() => { load(); }, [requestId]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/requests/${requestId}/photos?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} foto${files.length > 1 ? "s" : ""} subida${files.length > 1 ? "s" : ""}`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };

  const src = (p) => `${API}/files/${p.storage_path}`;

  const total = photos.before.length + photos.after.length;

  return (
    <div className="hub-card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-serif text-2xl">Fotos del trabajo</h3>
          <p className="text-xs text-[#5C6B7D] font-display">Sube pruebas antes y después. El cliente puede aprobar el trabajo viendo las fotos.</p>
        </div>
        {canUpload && (
          <div className="flex gap-2">
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="text-xs h-9 border border-[#0B1D3A]/15 rounded-full px-3" data-testid="photo-kind">
              <option value="before">Antes</option>
              <option value="after">Después</option>
            </select>
            <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="photo-input"/>
            <Button onClick={() => inputRef.current?.click()} disabled={uploading} className="hub-btn-primary gap-2 rounded-full" data-testid="photo-upload-btn">
              <Upload size={14}/> {uploading ? "Subiendo..." : "Subir fotos"}
            </Button>
          </div>
        )}
      </div>

      {total === 0 && (
        <div className="p-10 text-center text-[#5C6B7D] border border-dashed border-[#0B1D3A]/15 rounded-lg">
          <Camera className="mx-auto mb-2 text-[#5C6B7D]" size={24}/>
          <p className="text-sm">Aún no hay fotos. {canUpload && "Sube antes/después para que el cliente vea la calidad."}</p>
        </div>
      )}

      {["before", "after"].map((k) => (
        photos[k].length > 0 && (
          <div key={k} className="mt-4">
            <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display mb-2 flex items-center gap-2">
              {k === "before" ? "Antes" : <><Sparkles size={11} className="hub-coral"/> Después</>}
              <span className="text-[#0B1D3A]/40">({photos[k].length})</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {photos[k].map((p) => (
                <a key={p.id} href={src(p)} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden border border-[#0B1D3A]/10 hover:opacity-90 transition-opacity" data-testid={`photo-${p.id}`}>
                  <img src={src(p)} className="w-full h-full object-cover" alt="Trabajo"/>
                </a>
              ))}
            </div>
          </div>
        )
      ))}
    </div>
  );
}
